import { useCallback, useEffect, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Clock3, MessageSquareText, UserCircle2, Settings2 } from 'lucide-react';
import type { ServerBillingStatus, ServerMembership } from '@oracord/shared';
import { authClient } from '../lib/auth-client';
import { useMe } from '../hooks/useMe';
import { useSessionStore } from '../stores/session';
import { appSurface } from '../lib/web-ui-style';
import { apiFetch } from '../lib/api';
import { isBillingRoute, shouldRedirectToBilling, shouldWaitForActiveOrganizationRepair } from '../lib/onboarding-gates';
import { OrgSwitcher } from './OrgSwitcher';

// The signed-in chrome: left sidebar + top header (with org switcher)
// + <Outlet /> for the active page. Mirrors apps/desktop's shell layout
// on purpose so the two products feel like siblings.
//
// Responsibilities beyond layout:
//   - Fetch /api/me once on mount (via useMe).
//   - If the user has zero orgs, kick them to /onboarding/create-org —
//     no org means there's nothing to show.
//   - Ensure the active org in the zustand store is actually one the
//     user belongs to. If the persisted value is stale (a removed
//     membership, a different user on the same browser, etc.) we reset
//     it to the first real membership. Prevents stale X-Organization-Id
//     headers that would just earn us a 403.
export function AppShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const { me, loading, error } = useMe();
  const activeOrgId = useSessionStore(s => s.activeOrganizationId);
  const setActive = useSessionStore(s => s.setActiveOrganizationId);
  const [billing, setBilling] = useState<ServerBillingStatus | null>(null);
  const [billingLoading, setBillingLoading] = useState(false);
  useEffect(() => {
    if (!me) return;
    if (me.organizations.length === 0) {
      navigate('/onboarding/create-org', { replace: true });
      return;
    }
    const activeMembership = me.organizations.find(o => o.id === activeOrgId) ?? me.organizations[0];
    const stillMember = me.organizations.some(o => o.id === activeOrgId);
    if (!stillMember) {
      setActive(me.organizations[0].id);
      return;
    }
    if (activeMembership.role === 'member' && location.pathname !== '/staff/setup') {
      navigate('/staff/setup', { replace: true });
    }
  }, [me, activeOrgId, setActive, navigate, location.pathname]);

  const activeMembershipForBilling = me?.organizations.find(o => o.id === activeOrgId) ?? me?.organizations[0] ?? null;
  const canActiveOrgManageBilling = activeMembershipForBilling?.role === 'owner' || activeMembershipForBilling?.role === 'admin';

  const loadBilling = useCallback(async () => {
    if (!activeMembershipForBilling?.id || !canActiveOrgManageBilling) {
      setBilling(null);
      setBillingLoading(false);
      return;
    }
    setBillingLoading(true);
    try {
      setBilling(await apiFetch<ServerBillingStatus>('/api/billing/status'));
    } catch {
      setBilling(null);
    } finally {
      setBillingLoading(false);
    }
  }, [activeMembershipForBilling?.id, canActiveOrgManageBilling]);

  useEffect(() => {
    void loadBilling();
  }, [loadBilling]);

  useEffect(() => {
    if (!activeMembershipForBilling || billingLoading) return;
    if (shouldRedirectToBilling({
      pathname: location.pathname,
      search: location.search,
      role: activeMembershipForBilling.role,
      billing,
    })) {
      navigate(`/billing?onboarding=1&next=${encodeURIComponent('/setup')}`, { replace: true });
    }
  }, [activeMembershipForBilling, billing, billingLoading, location.pathname, location.search, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-slate-400">
        Loading…
      </div>
    );
  }

  if (error || !me) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8">
        <div className="max-w-sm rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          Could not load your account. {error?.message ?? 'Unknown error.'}
        </div>
      </div>
    );
  }

  // Zero-org branch is handled by the effect above (which kicks to
  // /onboarding/create-org). Returning null here prevents a flash of
  // empty shell before the redirect.
  if (me.organizations.length === 0) return null;

  const activeMembership = me.organizations.find(o => o.id === activeOrgId) ?? me.organizations[0];
  const repairingActiveOrg = shouldWaitForActiveOrganizationRepair({ activeOrgId, resolvedOrgId: activeMembership.id });
  if (repairingActiveOrg || (canActiveOrgManageBilling && billingLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-slate-400">
        Loading workspace…
      </div>
    );
  }

  async function onSignOut() {
    await authClient.signOut();
    useSessionStore.getState().clear();
    navigate('/login', { replace: true });
  }

  const billingLocked = canActiveOrgManageBilling && !billing?.active;

  return (
    <div className={`flex ${appSurface.page}`}>
      <Sidebar activeRole={activeMembership?.role ?? null} billingLocked={billingLocked} />
      <div className={`flex min-w-0 flex-1 flex-col ${appSurface.mainCanvas}`}>
        <Header
          userName={me.user.name || me.user.email}
          memberships={me.organizations}
          activeOrgId={activeMembership.id}
          canViewBilling={activeMembership.role === 'owner' || activeMembership.role === 'admin'}
          billing={billing}
          onSignOut={onSignOut}
        />
        <main className="flex-1 overflow-auto px-5 py-6 sm:px-8 lg:px-10">
          {billingLocked && !isBillingRoute(location.pathname) ? null : <Outlet />}
        </main>
      </div>
    </div>
  );
}

type SidebarItem = {
  label: string;
  to: string;
  adminOnly?: boolean;
  staffOnly?: boolean;
};

type SidebarGroup = {
  id: 'setup' | 'communications' | 'people';
  label: string;
  to: string;
  icon: typeof Clock3;
  paths: string[];
  shortcuts: SidebarItem[];
};

const sidebarGroups: SidebarGroup[] = [
  {
    id: 'setup',
    label: 'Setup',
    to: '/setup',
    icon: Clock3,
    paths: ['/', '/setup'],
    shortcuts: [],
  },
  {
    id: 'communications',
    label: 'Communications',
    to: '/communications',
    icon: MessageSquareText,
    paths: ['/communications'],
    shortcuts: [],
  },
  {
    id: 'people',
    label: 'People',
    to: '/contacts',
    icon: UserCircle2,
    paths: ['/contacts', '/team', '/members', '/staff/setup'],
    shortcuts: [
      { label: 'Contacts', to: '/contacts' },
      { label: 'Team', to: '/members' },
      { label: 'Work account', to: '/staff/setup', staffOnly: true },
    ],
  },
];

function Sidebar({ activeRole, billingLocked }: { activeRole: string | null; billingLocked: boolean }) {
  const location = useLocation();
  const canSeeSupport = activeRole === 'owner' || activeRole === 'admin';
  const isOwnerOrAdmin = activeRole === 'owner' || activeRole === 'admin';
  const visibleGroups = billingLocked ? [] : isOwnerOrAdmin ? sidebarGroups : sidebarGroups.filter(group => group.id === 'people');
  const navClass =
    'relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition';
  const shortcutClass = (to: string) => {
    const [path, search] = to.split('?');
    const pathActive = isPathActive(location.pathname, [path]);
    const active = search ? pathActive && new URLSearchParams(location.search).get('tab') === new URLSearchParams(search).get('tab') : pathActive;
    return `block rounded-lg px-3 py-1.5 text-xs font-medium transition ${active ? 'bg-white/10 text-white' : 'text-slate-400 hover:bg-white/5 hover:text-white'}`;
  };
  const activeGroup = visibleGroups.find(group => isPathActive(location.pathname, group.paths));
  const settingsActive = isPathActive(location.pathname, ['/settings']);

  return (
    <aside className={`flex w-60 flex-shrink-0 flex-col ${appSurface.sidebar}`}>
      <div className="border-b border-white/10 px-5 py-6">
        <div className="text-xl font-bold tracking-tight">Oracord</div>
        <div className="mt-1 text-xs font-medium uppercase tracking-[0.22em] text-slate-400">Ambient comms</div>
      </div>
      <nav className="flex-1 space-y-1 p-3">
        {billingLocked && (
          <div className="rounded-xl border border-amber-300/30 bg-amber-300/10 px-3 py-3 text-xs leading-5 text-amber-100">
            Subscribe to unlock Setup, Communications, People, and Settings.
          </div>
        )}
        {visibleGroups.map(group => {
          const Icon = group.icon;
          const active = activeGroup?.id === group.id;
          const shortcuts = group.shortcuts.filter(item => {
            if (item.adminOnly && !canSeeSupport) return false;
            if (item.staffOnly && isOwnerOrAdmin) return false;
            return true;
          });
          return (
            <div key={group.id}>
              <Link
                to={isOwnerOrAdmin ? group.to : '/staff/setup'}
                aria-current={active ? 'page' : undefined}
                className={`${navClass} ${active ? 'bg-white/10 text-white shadow-sm before:absolute before:left-0 before:top-2 before:h-6 before:w-1 before:rounded-full before:bg-emerald-300' : 'text-slate-300 hover:bg-white/5 hover:text-white'}`}
              >
                <Icon className="h-4 w-4" />
                {group.label}
              </Link>
              {active && shortcuts.length > 0 && (
                <div className="ml-7 mt-1 space-y-0.5 border-l border-white/10 pl-2">
                  {shortcuts.map(item => (
                    <Link key={item.to} to={item.to} className={shortcutClass(item.to)}>
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>
      {!billingLocked && (
        <div className="border-t border-white/10 p-3">
          <Link
            to="/settings"
            aria-current={settingsActive ? 'page' : undefined}
            className={`${navClass} w-full ${settingsActive ? 'bg-white/10 text-white shadow-sm before:absolute before:left-0 before:top-2 before:h-6 before:w-1 before:rounded-full before:bg-emerald-300' : 'text-slate-300 hover:bg-white/5 hover:text-white'}`}
          >
            <Settings2 className="h-4 w-4" /> Settings
          </Link>
        </div>
      )}
    </aside>
  );
}

function isPathActive(pathname: string, paths: string[]) {
  return paths.some(path => (path === '/' ? pathname === '/' : pathname === path || pathname.startsWith(`${path}/`)));
}

function Header({
  userName,
  memberships,
  activeOrgId,
  canViewBilling,
  billing,
  onSignOut,
}: {
  userName: string;
  memberships: ServerMembership[];
  activeOrgId: string;
  canViewBilling: boolean;
  billing: ServerBillingStatus | null;
  onSignOut: () => void;
}) {
  void activeOrgId;
  void canViewBilling;

  return (
    <header className="flex items-center justify-between border-b border-white/70 bg-white/80 px-5 py-3 shadow-sm shadow-slate-200/60 backdrop-blur sm:px-8 lg:px-10">
      <div>
        <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Workspace</div>
        <div className="mt-0.5 text-sm font-medium text-slate-700">Signed in as {userName}</div>
      </div>
      <div className="flex items-center gap-3">
        <OrgSwitcher memberships={memberships} billing={billing} canViewBilling={canViewBilling} onSignOut={onSignOut} />
      </div>
    </header>
  );
}
